/**
 * Phase J.3.2 — Full notifications list page. Renders for both roles via
 * the `role` prop; default-exported wrapper components live below for the
 * customer / admin route configs.
 */
import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronRight, CheckCheck, Settings2, Inbox } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { useNotificationsList } from "@/hooks/useNotifications";
import { getEventMeta } from "@/lib/notificationEvents";

function relTime(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diff = Math.max(0, Date.now() - then);
    const sec = Math.round(diff / 1000);
    if (sec < 45) return "just now";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 36) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 14) return `${day}d ago`;
    const wk = Math.round(day / 7);
    if (wk < 12) return `${wk}w ago`;
    return iso.slice(0, 10);
}


function FilterChip({ label, active, count, onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ring-1 ring-inset " +
                (active
                    ? "bg-blue-900 text-white ring-blue-900"
                    : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")
            }
        >
            {label}
            {typeof count === "number" && (
                <span className={"inline-block min-w-[18px] text-center px-1 rounded-full text-[10px] font-semibold " + (active ? "bg-white/20" : "bg-slate-100 text-slate-700")}>
                    {count}
                </span>
            )}
        </button>
    );
}


function Row({ row, onMarkRead, onOpen }) {
    const meta = getEventMeta(row.event_type);
    const Icon = meta.icon;
    const isRead = Boolean(row.read_at);
    return (
        <div
            data-testid={`notification-row-${row.id}`}
            className={"flex items-start gap-3 px-4 py-3.5 border-b border-slate-100 last:border-0 " + (isRead ? "bg-white" : "bg-blue-50/30")}
        >
            <span className={"mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-inset " + meta.accent}>
                <Icon size={16} aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <p className={"text-sm " + (isRead ? "text-slate-700 font-medium" : "text-slate-900 font-semibold")}>
                        {row.title}
                    </p>
                    <span className="text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200 rounded-full px-1.5 py-0.5 bg-white">
                        {meta.label}
                    </span>
                    {!isRead && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
                            <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-blue-600" />
                            unread
                        </span>
                    )}
                    <span className="ml-auto text-[11px] text-slate-400">{relTime(row.created_at)}</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">{row.body}</p>
                <div className="mt-2 flex items-center gap-3 text-xs">
                    {row.cta_url && (
                        <button
                            type="button"
                            data-testid={`notification-row-open-${row.id}`}
                            onClick={() => onOpen(row)}
                            className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium"
                        >
                            Open
                            <ChevronRight size={12} aria-hidden="true" />
                        </button>
                    )}
                    {!isRead && (
                        <button
                            type="button"
                            data-testid={`notification-row-mark-read-${row.id}`}
                            onClick={() => onMarkRead(row.id)}
                            className="text-slate-500 hover:text-slate-800"
                        >
                            Mark as read
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


export function NotificationsPage({ role }) {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("all"); // all | true | false
    const { items, unread, cursor, loading, error, refresh, loadMore, markRead, markAllRead } =
        useNotificationsList({ role, read: filter, limit: 20 });

    const totalCount = useMemo(() => items.length, [items]);
    const prefsHref = role === "customer" ? "/customer/notifications/preferences" : "/admin/notifications/preferences";

    const handleOpen = async (row) => {
        if (!row.read_at) await markRead(row.id);
        if (!row.cta_url) return;
        if (row.cta_url.startsWith("http")) {
            window.location.assign(row.cta_url);
        } else {
            navigate(row.cta_url);
        }
    };

    return (
        <DashboardShell>
            <div data-testid="notifications-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-0 py-6 space-y-5">
                <header className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Inbox</p>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Notifications</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {unread === 0
                                ? "You're all caught up."
                                : `${unread} unread · stays in sync every few seconds.`}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="notifications-mark-all"
                            onClick={markAllRead}
                            disabled={unread === 0}
                            className="gap-1.5"
                        >
                            <CheckCheck size={14} aria-hidden="true" />
                            Mark all as read
                        </Button>
                        <Link to={prefsHref}>
                            <Button type="button" variant="outline" size="sm" data-testid="notifications-prefs-link" className="gap-1.5">
                                <Settings2 size={14} aria-hidden="true" />
                                Preferences
                            </Button>
                        </Link>
                    </div>
                </header>

                <div className="flex items-center gap-2 flex-wrap">
                    <FilterChip
                        testId="notifications-filter-all"
                        label="All"
                        active={filter === "all"}
                        onClick={() => setFilter("all")}
                    />
                    <FilterChip
                        testId="notifications-filter-unread"
                        label="Unread"
                        count={unread}
                        active={filter === "false"}
                        onClick={() => setFilter("false")}
                    />
                    <FilterChip
                        testId="notifications-filter-read"
                        label="Read"
                        active={filter === "true"}
                        onClick={() => setFilter("true")}
                    />
                    <button
                        type="button"
                        data-testid="notifications-refresh"
                        onClick={refresh}
                        className="ml-auto text-xs font-medium text-slate-500 hover:text-slate-800"
                    >
                        Refresh
                    </button>
                </div>

                {error && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                        {error}
                    </div>
                )}

                <section className="bg-white rounded-lg ring-1 ring-slate-200 overflow-hidden">
                    {loading && items.length === 0 ? (
                        <div className="px-4 py-16 text-center text-sm text-slate-500">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="px-4 py-16 text-center">
                            <Inbox size={28} className="mx-auto text-slate-300" aria-hidden="true" />
                            <p className="text-sm text-slate-600 mt-3">No notifications yet.</p>
                            <p className="text-xs text-slate-400 mt-1">New activity appears here within a few seconds.</p>
                        </div>
                    ) : (
                        items.map((row) => (
                            <Row key={row.id} row={row} onMarkRead={markRead} onOpen={handleOpen} />
                        ))
                    )}
                </section>

                {cursor && (
                    <div className="text-center">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="notifications-load-more"
                            onClick={loadMore}
                            disabled={loading}
                        >
                            {loading ? "Loading…" : "Load more"}
                        </Button>
                    </div>
                )}

                <p className="text-[11px] text-slate-400 text-center pt-2">
                    Showing {totalCount} {totalCount === 1 ? "notification" : "notifications"}.
                </p>
            </div>
        </DashboardShell>
    );
}

export default NotificationsPage;
